// CatalogTaxesAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

/** ----------------------------------------------------------------
 * CatalogTaxesAdmin
 *
 * Purpose:
 *   Admin UI to maintain tax rates by country (by COUNTRY CODE).
 *
 * Backend:
 *   - GET    {backEndUrl}/catalog-taxes
 *            → { items: [ { Country, TaxRate, UpdatedAt? } ] }
 *            NOTE: Country is expected to be COUNTRY CODE (e.g., "US").
 *   - PUT    {backEndUrl}/catalog-taxes
 *            body: { Country, TaxRate }
 *   - DELETE {backEndUrl}/catalog-taxes
 *            body: { Country }
 *
 * Learn (role gate):
 *   - GET /learn/api/public/v1/users/me   (for institutionRoleIds)
 * ----------------------------------------------------------------*/

/** ---------------- i18n ---------------- */
const MESSAGES = {
    en_US: {
        gate: {
            checking: "Checking permissions…",
            blockedTitle: "Access Restricted",
            blockedMsg: "The Catalog Taxes Admin Tool is not available for your role.",
        },
        headings: {
            main: "Catalog Taxes Admin",
            list: "Existing Country Tax Rates",
            editor: "Tax Rate Editor",
            error: "Error",
        },
        buttons: {
            refreshList: "Refresh",
            newTax: "New",
            edit: "Edit",
            delete: "Delete",
            save: "Save",
            saved: "Saved",
            saving: "Saving…",
            cancel: "Cancel",
        },
        table: {
            country: "Country",
            countryCode: "Code",
            taxRate: "Tax Rate",
            updatedAt: "Updated",
            actions: "Actions",
            noResults: "No results",
        },
        form: {
            countryLabel: "Country",
            countryFilterLabel: "Find country",
            countryFilterPlaceholder: "Type to filter (e.g., united, states, us)…",
            countrySelectPlaceholder: "Select a country…",
            taxRateLabel: "Tax rate (%)",
            taxRatePlaceholder: "e.g., 7.25",
            help: "Tax rate is stored as a percent (0–100). Country is stored as the ISO code.",
        },
        notices: {
            loading: "Loading CatalogTaxesAdmin…",
            saved: "Tax rate saved.",
            failedSave: "Failed to save tax rate.",
            failedLoad: "Failed to load tax rates.",
            deleted: "Tax rate deleted.",
            failedDelete: "Failed to delete tax rate.",
        },
        confirm: {
            delete: "Delete this tax rate? This can’t be undone.",
        },
        validation: {
            missingCountry: "Country is required.",
            invalidRate: "Tax rate must be a number between 0 and 100.",
        },
        roleId: "CATALOG_TAXES_ADMIN",
    },
    en_AU: {},
};

const getMessages = (loc) => ({ ...MESSAGES.en_US, ...(MESSAGES[loc] || {}) });

/** ---------- Learn API adapter (role gate only) ---------- */
function makeLearnApi(platformUrl, token) {
    const base = (platformUrl || "").replace(/\/+$/, "");
    const http = axios.create({
        baseURL: base,
        timeout: 25000,
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    });

    const v1 = "/learn/api/public/v1";

    async function getCurrentUserInstitutionRoles() {
        const { data } = await http.get(`${v1}/users/me`);
        return data?.institutionRoleIds || [];
    }

    return { getCurrentUserInstitutionRoles };
}

/** ---------- Backend API adapter (tenant-aware) ---------- */
function makeBackendApi(backEndUrl, tenantId) {
    const base = (backEndUrl || "").replace(/\/+$/, "");
    const baseHeaders = { Accept: "application/json" };
    const http = axios.create({
        baseURL: base,
        timeout: 25000,
        headers: { ...baseHeaders, ...(tenantId ? { "x-tenant-id": tenantId } : {}) },
    });

    return {
        listTaxes: async () => {
            const { data } = await http.get(`/catalog-taxes`);
            if (Array.isArray(data?.items)) return data.items;
            if (Array.isArray(data)) return data;
            return [];
        },
        saveTax: async (payload) => {
            const { data } = await http.put(`/catalog-taxes`, payload);
            return data || {};
        },
        deleteTax: async (countryCode) => {
            await http.delete(`/catalog-taxes`, { data: { Country: countryCode } });
        },
    };
}

/** ---------- Country helpers ---------- */

// Fallback ISO-3166 alpha-2 list (used if Intl.supportedValuesOf isn't available)
const FALLBACK_COUNTRY_CODES = [
    "AD","AE","AF","AG","AI","AL","AM","AO","AQ","AR","AS","AT","AU","AW","AX","AZ",
    "BA","BB","BD","BE","BF","BG","BH","BI","BJ","BL","BM","BN","BO","BQ","BR","BS","BT","BV","BW","BY","BZ",
    "CA","CC","CD","CF","CG","CH","CI","CK","CL","CM","CN","CO","CR","CU","CV","CW","CX","CY","CZ",
    "DE","DJ","DK","DM","DO","DZ",
    "EC","EE","EG","EH","ER","ES","ET",
    "FI","FJ","FK","FM","FO","FR",
    "GA","GB","GD","GE","GF","GG","GH","GI","GL","GM","GN","GP","GQ","GR","GS","GT","GU","GW","GY",
    "HK","HM","HN","HR","HT","HU",
    "ID","IE","IL","IM","IN","IO","IQ","IR","IS","IT",
    "JE","JM","JO","JP",
    "KE","KG","KH","KI","KM","KN","KP","KR","KW","KY","KZ",
    "LA","LB","LC","LI","LK","LR","LS","LT","LU","LV","LY",
    "MA","MC","MD","ME","MF","MG","MH","MK","ML","MM","MN","MO","MP","MQ","MR","MS","MT","MU","MV","MW","MX","MY","MZ",
    "NA","NC","NE","NF","NG","NI","NL","NO","NP","NR","NU","NZ",
    "OM",
    "PA","PE","PF","PG","PH","PK","PL","PM","PN","PR","PS","PT","PW","PY",
    "QA",
    "RE","RO","RS","RU","RW",
    "SA","SB","SC","SD","SE","SG","SH","SI","SJ","SK","SL","SM","SN","SO","SR","SS","ST","SV","SX","SY","SZ",
    "TC","TD","TF","TG","TH","TJ","TK","TL","TM","TN","TO","TR","TT","TV","TW","TZ",
    "UA","UG","UM","US","UY","UZ",
    "VA","VC","VE","VG","VI","VN","VU",
    "WF","WS",
    "YE","YT",
    "ZA","ZM","ZW"
];

function toBcp47(locale) {
    return String(locale || "en_US").replace("_", "-");
}

function buildCountryOptions(locale) {
    const bcp47 = toBcp47(locale);
    const dn = typeof Intl !== "undefined" && Intl.DisplayNames ? new Intl.DisplayNames([bcp47], { type: "region" }) : null;

    let codes = [];
    try {
        if (typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function") {
            codes = Intl.supportedValuesOf("region")
                .map((c) => String(c || "").toUpperCase())
                .filter((c) => /^[A-Z]{2}$/.test(c));
        }
    } catch {
        // ignore
    }
    if (!codes.length) codes = FALLBACK_COUNTRY_CODES.slice();

    const seen = new Set();
    const out = [];
    for (const code of codes) {
        const c = String(code || "").toUpperCase();
        if (!/^[A-Z]{2}$/.test(c) || seen.has(c)) continue;
        seen.add(c);
        const name = dn ? dn.of(c) : c;
        out.push({ code: c, name: name || c });
    }

    out.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
    return out;
}

function countryNameFromCode(code, options) {
    const c = String(code || "").toUpperCase();
    const hit = options.find((o) => o.code === c);
    return hit?.name || c || "";
}

/** ---------- Utilities ---------- */
function normalizeTaxItem(item, countryOptions) {
    if (!item) return null;

    // "Country" is now treated as country code (e.g., "US")
    const countryCode =
        item.CountryCode ||
        item.countryCode ||
        item.Country ||
        item.country ||
        "";

    const taxRate =
        item.TaxRate ??
        item.taxRate ??
        item.Rate ??
        item.rate ??
        "";

    const updatedAt = item.UpdatedAt || item.updatedAt || null;

    const code = String(countryCode || "").trim().toUpperCase();
    if (!code) return null;

    return {
        countryCode: code,
        countryName: countryNameFromCode(code, countryOptions),
        taxRate: taxRate === "" || taxRate === null || taxRate === undefined ? "" : String(taxRate),
        updatedAt,
    };
}

function parseRate(raw) {
    const s = String(raw ?? "").trim();
    if (!s) return null;
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    return n;
}

/** ---------- Component ---------- */
const CatalogTaxesAdmin = ({ platformUrl, backEndUrl, locale = "en_US", tenantId }) => {
    const M = useMemo(() => getMessages(locale), [locale]);

    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const token = queryParams.get("token") || "";

    const learn = useMemo(() => makeLearnApi(platformUrl, token), [platformUrl, token]);
    const backend = useMemo(() => makeBackendApi(backEndUrl, tenantId), [backEndUrl, tenantId]);

    // Country options (all countries)
    const countryOptions = useMemo(() => buildCountryOptions(locale), [locale]);

    // Gate
    const [roleChecked, setRoleChecked] = useState(false);
    const [hasAdminRole, setHasAdminRole] = useState(false);

    // Data
    const [loading, setLoading] = useState(true);
    const [fatalError, setFatalError] = useState("");

    const [inlineError, setInlineError] = useState("");
    const [inlineSuccess, setInlineSuccess] = useState("");

    const [taxes, setTaxes] = useState([]);

    // Editor
    const [current, setCurrent] = useState(null); // { countryCode, taxRate }
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    // Country filter (for usability with large dropdowns)
    const [countryFilter, setCountryFilter] = useState("");

    const filteredCountryOptions = useMemo(() => {
        const term = String(countryFilter || "").trim().toLowerCase();
        if (!term) return countryOptions;

        // match name or code
        return countryOptions.filter((o) => {
            const code = (o.code || "").toLowerCase();
            const name = (o.name || "").toLowerCase();
            return code.includes(term) || name.includes(term);
        });
    }, [countryOptions, countryFilter]);

    const compactCss = `
    .bb-container { font-size: 13px; }
    .bb-card h2 { font-size: 16px; font-weight: 700 !important; }
    .bb-table { font-size: 12px; border-collapse: collapse; width: 100%; }
    .bb-table th, .bb-table td { padding: 6px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
    .bb-table th { font-weight: 600; text-align: left; }
    .bb-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }

    .bb-card .bb-button,
    .bb-container .bb-button {
      -webkit-appearance: none !important;
      appearance: none !important;
      background: #f6f6f6 !important;
      border: 1px solid #d3d3d3 !important;
      color: #222 !important;
      border-radius: 6px !important;
      padding: 6px 10px !important;
      font: inherit !important;
      line-height: 1.2 !important;
      cursor: pointer !important;
    }
    .bb-card .bb-button:hover,
    .bb-container .bb-button:hover { background: #efefef !important; }
    .bb-card .bb-button:focus-visible { outline: 2px solid #5a9cff !important; outline-offset: 2px !important; }

    .bb-card .bb-button.danger { background: #fff1f1 !important; border-color: #e6b3b3 !important; color: #7a0a0a !important; }
    .bb-card .bb-button.danger:hover { background: #fee !important; }
    .bb-card .bb-button.secondary { background: #f2f2f2 !important; border-color: #d0d0d0 !important; color: #222 !important; }

    select, input { font-size: 12px; padding: 4px 6px; height: 30px; font-weight: 400; color: inherit; }
  `;

    const Flash = ({ type, children }) => {
        if (!children) return null;
        const styles = {
            marginBottom: 6,
            padding: "8px 10px",
            borderRadius: 6,
            border: "1px solid",
            background: type === "error" ? "#ffecec" : "#ecfff1",
            borderColor: type === "error" ? "#ffb3b3" : "#b3ffcc",
            color: type === "error" ? "#a40000" : "#005c2e",
            fontSize: 12,
        };
        return (
            <div role={type === "error" ? "alert" : "status"} style={styles} aria-live="polite">
                {children}
            </div>
        );
    };

    /** ---------------- Role gate ---------------- */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (!platformUrl || !token) {
                    if (!cancelled) {
                        setHasAdminRole(false);
                        setRoleChecked(true);
                    }
                    return;
                }
                const roles = await learn.getCurrentUserInstitutionRoles();
                if (!cancelled) {
                    setHasAdminRole(Array.isArray(roles) && roles.includes(M.roleId));
                    setRoleChecked(true);
                }
            } catch {
                if (!cancelled) {
                    setHasAdminRole(false);
                    setRoleChecked(true);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [learn, platformUrl, token, M.roleId]);

    /** ---------------- Load list ---------------- */
    async function loadTaxes({ clearMessages = false, suppressSpinner = false } = {}) {
        if (!suppressSpinner) setLoading(true);
        setFatalError("");
        if (clearMessages) {
            setInlineError("");
            setInlineSuccess("");
        }

        try {
            const items = await backend.listTaxes();
            const normalized = (items || [])
                .map((it) => normalizeTaxItem(it, countryOptions))
                .filter(Boolean);

            // Sort by display name for a nicer UX
            normalized.sort((a, b) => (a.countryName || "").localeCompare(b.countryName || ""));

            setTaxes(normalized);

            // Keep editor selection if possible
            setCurrent((prev) => {
                if (!prev?.countryCode) return prev;
                const match = normalized.find((t) => t.countryCode === prev.countryCode);
                return match ? { countryCode: match.countryCode, taxRate: match.taxRate } : prev;
            });
        } catch (err) {
            setFatalError(err?.response?.data?.message || err.message || M.notices.failedLoad);
        } finally {
            if (!suppressSpinner) setLoading(false);
        }
    }

    useEffect(() => {
        if (roleChecked && hasAdminRole) {
            loadTaxes({ clearMessages: true, suppressSpinner: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleChecked, hasAdminRole, backEndUrl, tenantId, countryOptions]);

    /** ---------------- Editor helpers ---------------- */
    function startNew() {
        setCurrent({ countryCode: "", taxRate: "" });
        setDirty(false);
        setInlineError("");
        setInlineSuccess("");
        setCountryFilter("");
    }

    function editExisting(countryCode) {
        const code = String(countryCode || "").toUpperCase();
        const t = taxes.find((x) => x.countryCode === code);
        if (!t) return;
        setCurrent({ countryCode: t.countryCode, taxRate: t.taxRate });
        setDirty(false);
        setInlineError("");
        setInlineSuccess("");
        setCountryFilter("");
    }

    function cancelEdit() {
        setCurrent(null);
        setDirty(false);
        setInlineError("");
        setInlineSuccess("");
        setCountryFilter("");
    }

    function updateField(field, value) {
        setCurrent((prev) => ({ ...(prev || {}), [field]: value }));
        setDirty(true);
    }

    /** ---------------- Save/Delete ---------------- */
    async function saveCurrent() {
        setInlineError("");
        setInlineSuccess("");

        if (!current) return;

        const countryCode = String(current.countryCode || "").trim().toUpperCase();
        if (!countryCode) {
            setInlineError(M.validation.missingCountry);
            return;
        }

        const rateNum = parseRate(current.taxRate);
        if (rateNum === null || rateNum < 0 || rateNum > 100) {
            setInlineError(M.validation.invalidRate);
            return;
        }

        setSaving(true);
        try {
            // Store the COUNTRY CODE in Country
            await backend.saveTax({ Country: countryCode, TaxRate: rateNum });
            setInlineSuccess(M.notices.saved);
            setDirty(false);
            await loadTaxes({ suppressSpinner: true });
            editExisting(countryCode);
        } catch (err) {
            setInlineError(err?.response?.data?.message || err.message || M.notices.failedSave);
        } finally {
            setSaving(false);
        }
    }

    async function deleteTax(countryCode) {
        const okConfirm = window.confirm(M.confirm.delete);
        if (!okConfirm) return;

        setInlineError("");
        setInlineSuccess("");

        try {
            await backend.deleteTax(countryCode);
            setTaxes((prev) => prev.filter((t) => t.countryCode !== String(countryCode || "").toUpperCase()));
            setInlineSuccess(M.notices.deleted);

            if (current?.countryCode === String(countryCode || "").toUpperCase()) {
                setCurrent(null);
                setDirty(false);
            }
        } catch (err) {
            setInlineError(err?.response?.data?.message || err.message || M.notices.failedDelete);
        }
    }

    /** ---------------- Gates ---------------- */
    if (!roleChecked) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div>{M.gate.checking}</div>
            </div>
        );
    }

    if (!hasAdminRole) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                    <h2 style={{ margin: 0 }}>{M.gate.blockedTitle}</h2>
                    <p style={{ fontSize: 12 }}>{M.gate.blockedMsg}</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div>{M.notices.loading}</div>
            </div>
        );
    }

    if (fatalError) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                    <h2 style={{ margin: 0 }}>{M.headings.error}</h2>
                    <p style={{ fontSize: 12 }}>{fatalError}</p>
                </div>
            </div>
        );
    }

    /** ---------------- Main UI ---------------- */
    return (
        <div className="bb-container" style={{ display: "grid", gap: 12 }}>
            <style dangerouslySetInnerHTML={{ __html: compactCss }} />

            {/* List */}
            <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                <div className="bb-row" style={{ justifyContent: "space-between", marginBottom: 4 }}>
                    <h2 style={{ margin: 0 }}>{M.headings.list}</h2>
                    <div className="bb-row">
                        <button
                            type="button"
                            className="bb-button secondary"
                            onClick={() => loadTaxes({ clearMessages: true, suppressSpinner: false })}
                        >
                            {M.buttons.refreshList}
                        </button>
                        <button type="button" className="bb-button" onClick={startNew}>
                            {M.buttons.newTax}
                        </button>
                    </div>
                </div>

                <table className="bb-table" role="grid">
                    <thead>
                    <tr>
                        <th>{M.table.country}</th>
                        <th style={{ width: 80 }}>{M.table.countryCode}</th>
                        <th>{M.table.taxRate}</th>
                        <th>{M.table.updatedAt}</th>
                        <th style={{ width: 210 }}>{M.table.actions}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {taxes.length === 0 ? (
                        <tr>
                            <td colSpan={5} style={{ textAlign: "center" }}>
                                {M.table.noResults}
                            </td>
                        </tr>
                    ) : (
                        taxes.map((t) => (
                            <tr key={t.countryCode}>
                                <td>{t.countryName || t.countryCode || "-"}</td>
                                <td>{t.countryCode || "-"}</td>
                                <td>{t.taxRate === "" ? "-" : `${t.taxRate}%`}</td>
                                <td>{t.updatedAt ? String(t.updatedAt).slice(0, 19) : "-"}</td>
                                <td>
                                    <div className="bb-row">
                                        <button type="button" className="bb-button" onClick={() => editExisting(t.countryCode)}>
                                            {M.buttons.edit}
                                        </button>
                                        <button type="button" className="bb-button danger" onClick={() => deleteTax(t.countryCode)}>
                                            {M.buttons.delete}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            {/* Editor */}
            <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                <div className="bb-row" style={{ justifyContent: "space-between", marginBottom: 4 }}>
                    <h2 style={{ margin: 0 }}>{M.headings.editor}</h2>

                    <div className="bb-row">
                        <button
                            className="bb-button"
                            onClick={saveCurrent}
                            disabled={!current || !dirty || saving}
                            aria-disabled={!current || !dirty || saving}
                            title={M.buttons.save}
                        >
                            {saving ? M.buttons.saving : dirty ? M.buttons.save : M.buttons.saved}
                        </button>

                        <button className="bb-button secondary" onClick={cancelEdit} disabled={!current || saving}>
                            {M.buttons.cancel}
                        </button>
                    </div>
                </div>

                <Flash type="error">{inlineError}</Flash>
                <Flash type="success">{inlineSuccess}</Flash>

                {!current ? (
                    <p style={{ fontSize: 12, marginTop: 4 }}>
                        Select an existing country to edit, or click <b>{M.buttons.newTax}</b>.
                    </p>
                ) : (
                    <div style={{ display: "grid", gap: 8 }}>
                        <div style={{ padding: 8, borderRadius: 6, border: "1px solid #eee" }}>
                            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                                {M.form.countryLabel}
                            </label>

                            <div className="bb-row" style={{ alignItems: "flex-end" }}>
                                <div style={{ flex: 1, minWidth: 240 }}>
                                    <label style={{ display: "block", fontSize: 11, marginBottom: 4, color: "#555" }}>
                                        {M.form.countryFilterLabel}
                                    </label>
                                    <input
                                        type="text"
                                        value={countryFilter}
                                        onChange={(e) => setCountryFilter(e.target.value)}
                                        placeholder={M.form.countryFilterPlaceholder}
                                        style={{ width: "100%" }}
                                        disabled={saving}
                                    />
                                </div>

                                <div style={{ flex: 2, minWidth: 260 }}>
                                    <label style={{ display: "block", fontSize: 11, marginBottom: 4, color: "#555" }}>
                                        {M.form.countryLabel}
                                    </label>
                                    <select
                                        value={String(current.countryCode || "").toUpperCase()}
                                        onChange={(e) => updateField("countryCode", e.target.value)}
                                        style={{ width: "100%" }}
                                        disabled={saving}
                                    >
                                        <option value="">{M.form.countrySelectPlaceholder}</option>
                                        {filteredCountryOptions.map((o) => (
                                            <option key={o.code} value={o.code}>
                                                {o.name} ({o.code})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {current.countryCode ? (
                                <div style={{ marginTop: 6, fontSize: 11, color: "#555" }}>
                                    Selected: <b>{countryNameFromCode(current.countryCode, countryOptions)}</b> ({String(current.countryCode).toUpperCase()})
                                </div>
                            ) : null}
                        </div>

                        <div style={{ padding: 8, borderRadius: 6, border: "1px solid #eee" }}>
                            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                                {M.form.taxRateLabel}
                            </label>
                            <input
                                type="number"
                                min={0}
                                max={100}
                                step="0.01"
                                value={current.taxRate ?? ""}
                                onChange={(e) => updateField("taxRate", e.target.value)}
                                placeholder={M.form.taxRatePlaceholder}
                                style={{ width: 200 }}
                                disabled={saving}
                            />
                            <div style={{ fontSize: 11, marginTop: 6, color: "#555" }}>{M.form.help}</div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CatalogTaxesAdmin;